import dayjs, { Dayjs } from 'dayjs';

/**
 * The date scope for a CI working queue.
 *
 * Defaulting to 30 days changes what a tab *is*: it becomes "people I should
 * still be working" rather than "everyone at this stage", which is the right
 * default for a daily queue — but it also hides the win-back population
 * entirely. Widening the range is the only way back to them, which is why the
 * control has to stay obvious and why 'all' is one of the presets.
 *
 * Pre-consultation may want a 60- or 90-day default while post-consultation
 * stays at 30 — an intro call that produced a High and no booking is worth
 * chasing well past a month. That is why the default is the caller's choice
 * rather than baked in here.
 */
export type ListRangePresetId =
  | 'last-7'
  | 'last-30'
  | 'last-60'
  | 'last-90'
  | 'upcoming'
  | 'next-7'
  | 'next-30'
  | 'this-month'
  | 'last-month'
  | 'month-before-last'
  | 'this-quarter'
  | 'last-quarter'
  | 'year-to-date'
  | 'all'
  | 'custom';

/** `YYYY-MM-DD` bounds, both inclusive. An absent bound is unbounded on that side. */
export interface ListRange {
  from?: string;
  to?: string;
}

export const LIST_RANGE_PRESETS: Array<{ id: ListRangePresetId; label: string }> = [
  { id: 'last-30', label: 'Last 30 days' },
  { id: 'last-60', label: 'Last 60 days' },
  { id: 'last-90', label: 'Last 90 days' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom range' },
];

// `last-7` is offered only by the tabs that ask for it, not in LIST_RANGE_PRESETS.
const DAYS_BY_PRESET: Partial<Record<ListRangePresetId, number>> = {
  'last-7': 7,
  'last-30': 30,
  'last-60': 60,
  'last-90': 90,
};

// Forward-looking, for Pre-consultation; each starts today.
const DAYS_AHEAD_BY_PRESET: Partial<Record<ListRangePresetId, number>> = {
  'next-7': 7,
  'next-30': 30,
};

// Calendar periods, for Won; each runs to today.
const PERIOD_START_BY_PRESET: Partial<Record<ListRangePresetId, (today: Dayjs) => Dayjs>> = {
  'this-month': (today) => today.startOf('month'),
  'this-quarter': (today) => today.startOf('year').month(Math.floor(today.month() / 3) * 3),
  'year-to-date': (today) => today.startOf('year'),
};

const RANGE_DATE_FORMAT = 'YYYY-MM-DD';

export function rangeForPreset(preset: ListRangePresetId, now: Date): ListRange {
  const today = dayjs(now);
  const daysAhead = DAYS_AHEAD_BY_PRESET[preset];

  if (preset === 'upcoming') {
    return { from: today.format(RANGE_DATE_FORMAT) };
  }

  const periodStart = PERIOD_START_BY_PRESET[preset];
  if (periodStart) {
    return { from: periodStart(today).format(RANGE_DATE_FORMAT), to: today.format(RANGE_DATE_FORMAT) };
  }

  // Whole past months and the last whole quarter, for the Revenue ledger.
  const monthsBack = { 'last-month': 1, 'month-before-last': 2 }[preset as string];
  if (monthsBack !== undefined) {
    const month = today.subtract(monthsBack, 'month');
    return {
      from: month.startOf('month').format(RANGE_DATE_FORMAT),
      to: month.endOf('month').format(RANGE_DATE_FORMAT),
    };
  }

  if (preset === 'last-quarter') {
    const quarterStart = today
      .startOf('month')
      .month(Math.floor(today.month() / 3) * 3)
      .subtract(3, 'month');
    return {
      from: quarterStart.format(RANGE_DATE_FORMAT),
      to: quarterStart.add(2, 'month').endOf('month').format(RANGE_DATE_FORMAT),
    };
  }

  if (daysAhead !== undefined) {
    return {
      from: today.format(RANGE_DATE_FORMAT),
      to: today.add(daysAhead - 1, 'day').format(RANGE_DATE_FORMAT),
    };
  }

  const days = DAYS_BY_PRESET[preset];

  if (days === undefined) {
    return {};
  }

  // `days - 1` because the window includes today: 30 days ending today spans
  // today and the 29 before it.
  return {
    from: today.subtract(days - 1, 'day').format(RANGE_DATE_FORMAT),
    to: today.format(RANGE_DATE_FORMAT),
  };
}

/**
 * The range as the instants a query can be bounded by: `[start, end)`.
 *
 * Medplum resolves a date-only search value to midnight UTC of that date, on
 * BOTH sides of the comparison. `date le "2026-09-15"` therefore means
 * `date <= 2026-09-15T00:00:00Z` and returns nothing from the 15th itself,
 * which is how the post-consultation queue never showed a consult newer than
 * yesterday (BAR-2708; verified on staging 2026-09-16 — the same search with
 * `lt "2026-09-16"` returned the day's consults). FHIR's spec says a date-only
 * bound covers the whole day; Medplum's search does not, and neither does its
 * subscription matcher.
 *
 * So the inclusive `to` day becomes an exclusive bound on the next local
 * midnight, sent as a full instant. Local, not UTC: a rep's "today" ends at
 * their midnight, and a 6pm Pacific consult is 01:00Z tomorrow.
 */
export interface ListRangeBounds {
  /** Inclusive, ISO instant. */
  start?: string;
  /** Exclusive, ISO instant. */
  end?: string;
}

export function listRangeBounds(range: ListRange): ListRangeBounds {
  return {
    start: range.from ? dayjs(range.from).startOf('day').toISOString() : undefined,
    end: range.to ? dayjs(range.to).startOf('day').add(1, 'day').toISOString() : undefined,
  };
}

/**
 * The UTC days the range covers, for a day tag (`signup-day`, `stage-day`). A
 * local range straddles UTC days, so this can include one extra day at an edge;
 * rows trim to the exact instant. Empty for an unbounded start (All time).
 */
export function rangeDayCodes(range: ListRange): string[] {
  const { start, end } = listRangeBounds(range);
  if (!start) {
    return [];
  }
  const lastDay = new Date(new Date(end ?? Date.now()).getTime() - 1).toISOString().slice(0, 10);
  const days: string[] = [];
  for (let day = new Date(`${start.slice(0, 10)}T00:00:00.000Z`); ; day.setUTCDate(day.getUTCDate() + 1)) {
    const code = day.toISOString().slice(0, 10);
    if (code > lastDay) {
      break;
    }
    days.push(code);
  }
  return days;
}

/** The range as one `_filter` clause on a Patient day tag; undefined for All time. */
export function dayTagClause(system: string, range: ListRange): string | undefined {
  const days = rangeDayCodes(range);
  return days.length > 0 ? `(${days.map((day) => `_tag eq "${system}|${day}"`).join(' or ')})` : undefined;
}

/**
 * The range as `_filter` clauses on the `date` search parameter, for the
 * queues' GraphQL queries. Empty for an unbounded range.
 */
export function dateRangeFilterClauses(range: ListRange): string[] {
  const { start, end } = listRangeBounds(range);
  const clauses: string[] = [];

  if (start) {
    clauses.push(`date ge "${start}"`);
  }

  if (end) {
    clauses.push(`date lt "${end}"`);
  }

  return clauses;
}

/**
 * Whether an instant falls inside the range, on the same bounds the query
 * sends. The row builders need this because a live update is merged on a
 * coarser criteria than the query (see `useLiveGraphQL`'s `subscriptionParams`),
 * so what arrives by subscription is scoped here, not by the server.
 */
export function isWithinListRange(instant: string | undefined, range: ListRange): boolean {
  if (!instant) {
    return false;
  }

  const { start, end } = listRangeBounds(range);
  const at = new Date(instant).getTime();

  return (!start || at >= new Date(start).getTime()) && (!end || at < new Date(end).getTime());
}
